import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { AlertChip, EstadoChip } from "@/components/AlertChip";
import LoteForm from "@/components/LoteForm";
import ProductoForm from "@/components/ProductoForm";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, PencilSimple, Eye, Trash, CircleNotch, Package, ArrowUp, ArrowDown } from "@phosphor-icons/react";

function Stat({ label, value, onClick, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`bg-white border border-[#E2E8F0] rounded-md px-5 py-4 ${onClick ? "cursor-pointer hover:border-[#0F172A] hover:shadow-sm transition-all" : ""}`}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <p className="text-2xl font-black tracking-tight text-[#0F172A] font-heading mt-1">{value}</p>
    </div>
  );
}

export default function ProductoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loteFormOpen, setLoteFormOpen] = useState(false);
  const [editingLote, setEditingLote] = useState(null);
  const [prodFormOpen, setProdFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [txOpen, setTxOpen] = useState(false);
  const [txMovs, setTxMovs] = useState(null);

  const openTransacciones = () => {
    setTxOpen(true);
    setTxMovs(null);
    api.get(`/movimientos?producto_id=${id}&limit=500`).then((r) => setTxMovs(r.data));
  };

  const load = useCallback(() => {
    api.get(`/productos/${id}`).then((r) => setData(r.data));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteLote = async () => {
    try {
      await api.delete(`/lotes/${toDelete.id}`);
      toast.success("Lote eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
      </div>
    );
  }

  const { producto, lotes } = data;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="producto-detail-page">
      <button onClick={() => navigate("/productos")} data-testid="back-to-productos" className="inline-flex items-center gap-2 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors">
        <ArrowLeft size={18} weight="bold" /> Volver a productos
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-md bg-[#F1F5F9] flex items-center justify-center shrink-0">
            <Package size={24} weight="duotone" className="text-[#0F172A]" />
          </span>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-[#0F172A]">{producto.nombre}</h1>
            <p className="text-[#475569] mt-0.5">{producto.codigo} · {producto.tipo}{producto.descripcion ? ` · ${producto.descripcion}` : ""}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]" data-testid="producto-reorden">
                Punto de reorden: {fmtNumber(producto.reorder_point)} u{producto.stock_minimo == null ? " (global)" : ""}
              </span>
              {producto.unidades_por_embalaje != null && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#EEF2FF] text-[#3730A3]" data-testid="producto-embalaje-chip">
                  {fmtNumber(producto.unidades_por_embalaje)} u/{producto.tipo_embalaje || "embalaje"}
                </span>
              )}
              {producto.origen && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#F0FDF4] text-[#166534] capitalize" data-testid="producto-origen-chip">
                  {producto.origen}
                </span>
              )}
              {producto.categoria && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1D4ED8]" data-testid="producto-categoria-chip">
                  {producto.categoria}
                </span>
              )}
              {producto.stock_total <= producto.reorder_point && (
                <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]" data-testid="producto-critico-badge">
                  Stock crítico
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setProdFormOpen(true)} data-testid="edit-producto-button" className="border-[#E2E8F0]">
            <PencilSimple size={18} className="mr-1.5" /> Editar producto
          </Button>
          <Button onClick={() => { setEditingLote(null); setLoteFormOpen(true); }} data-testid="add-lote-button" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
            <Plus size={18} weight="bold" className="mr-1.5" /> Agregar lote
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Lotes" value={fmtNumber(producto.num_lotes)} />
        <Stat label="Cantidad disponible" value={fmtNumber(producto.stock_total)} />
        <Stat label="Transacciones" value={fmtNumber(producto.num_transacciones)} onClick={openTransacciones} testid="producto-transacciones-card" />
        <Stat label="Alertas activas" value={fmtNumber(producto.alertas)} />
        <Stat label="Costo promedio" value={fmtMoney(producto.costo_promedio)} />
        <Stat label="Costo por embalaje" value={fmtMoney(producto.costo_embalaje_promedio)} />
        <Stat label="Valorización stock" value={fmtMoney(producto.valor)} />
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E8F0]">
          <h2 className="font-heading text-lg font-bold text-[#0F172A]">Lotes</h2>
        </div>
        {lotes.length === 0 ? (
          <p className="px-6 py-12 text-center text-[#64748B]">Este producto aún no tiene lotes. Agrega uno.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="producto-lotes-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                  <th className="px-5 py-3 font-semibold">Nº lote</th>
                  <th className="px-5 py-3 font-semibold">Proveedor</th>
                  <th className="px-5 py-3 font-semibold">Vencimiento</th>
                  <th className="px-5 py-3 font-semibold text-right">Cant. inicial</th>
                  <th className="px-5 py-3 font-semibold text-right">Stock</th>
                  <th className="px-5 py-3 font-semibold text-right">Costo u.</th>
                  <th className="px-5 py-3 font-semibold text-right">Costo embalaje</th>
                  <th className="px-5 py-3 font-semibold text-right">Valorización</th>
                  <th className="px-5 py-3 font-semibold">Estado</th>
                  <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((l) => (
                  <tr key={l.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                    <td className="px-5 py-3 font-semibold text-[#0F172A]">{l.numero_lote}</td>
                    <td className="px-5 py-3 text-[#475569]">{l.proveedor_nombre || "—"}</td>
                    <td className="px-5 py-3">
                      {l.fecha_vencimiento ? (
                        <>
                          <div className="text-[#0F172A]">{fmtDate(l.fecha_vencimiento)}</div>
                          <div className="mt-1"><AlertChip level={l.alert_level} dias={l.dias_hasta_vencimiento} /></div>
                        </>
                      ) : (
                        <span className="text-xs text-[#94A3B8]">Sin vencimiento</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right text-[#475569]" data-testid={`lote-cant-inicial-${l.id}`}>{fmtNumber(l.cantidad_inicial)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(l.stock_actual)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]">{fmtMoney(l.costo_unitario)}</td>
                    <td className="px-5 py-3 text-right text-[#475569]">{fmtMoney(l.costo_embalaje)}</td>
                    <td className="px-5 py-3 text-right text-[#0F172A]">{fmtMoney(l.valor)}</td>
                    <td className="px-5 py-3"><EstadoChip estado={l.estado} /></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`view-lote-${l.id}`} onClick={() => navigate(`/lotes/${l.id}`)} className="p-2 rounded-md text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]" title="Ver movimientos">
                          <Eye size={18} />
                        </button>
                        <button data-testid={`edit-lote-${l.id}`} onClick={() => { setEditingLote(l); setLoteFormOpen(true); }} className="p-2 rounded-md text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]" title="Editar">
                          <PencilSimple size={18} />
                        </button>
                        <button data-testid={`delete-lote-${l.id}`} onClick={() => setToDelete(l)} className="p-2 rounded-md text-[#475569] hover:bg-[#FEF2F2] hover:text-[#991B1B]" title="Eliminar">
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LoteForm open={loteFormOpen} onOpenChange={setLoteFormOpen} lote={editingLote} productoId={editingLote ? undefined : id} onSaved={load} />
      <ProductoForm open={prodFormOpen} onOpenChange={setProdFormOpen} producto={producto} onSaved={load} />

      <Dialog open={txOpen} onOpenChange={setTxOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="producto-transacciones-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
              Historial de movimientos · {producto.nombre}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Todas las transacciones (ingresos y salidas) de los lotes de este producto.
            </DialogDescription>
          </DialogHeader>
          {txMovs === null ? (
            <div className="flex items-center justify-center py-16">
              <CircleNotch size={28} className="animate-spin text-[#0F172A]" />
            </div>
          ) : txMovs.length === 0 ? (
            <p className="py-12 text-center text-[#64748B]">Este producto aún no tiene movimientos.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="producto-transacciones-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                    <th className="px-4 py-2.5 font-semibold">Lote</th>
                    <th className="px-4 py-2.5 font-semibold">Tipo</th>
                    <th className="px-4 py-2.5 font-semibold">Motivo</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Cant.</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {txMovs.map((m) => (
                    <tr key={m.id} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-2.5 text-[#64748B]">{m.numero_lote}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${m.tipo === "ingreso" ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                          {m.tipo === "ingreso" ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" />}
                          {m.tipo === "ingreso" ? "Ingreso" : "Salida"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[#475569]">{m.motivo}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-[#0F172A]">{fmtNumber(m.cantidad)}</td>
                      <td className="px-4 py-2.5 text-right text-[#64748B]">{fmtDate(m.fecha_movimiento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-lote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este lote?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el lote "{toDelete?.numero_lote}" y todos sus movimientos. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-lote-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-lote-confirm" onClick={deleteLote} className="bg-[#DC2626] hover:bg-[#991B1B]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
